import {
  BooleanField,
  IModelContext,
  IValidationResult,
  ModelDependency,
  ModelGroup,
  ModelItem,
  NumberField,
  StringField,
  ValueOptions
} from '@mng/model';
import { StammdatenBase } from '../allgemein/stammdaten-base';
import {
  berufMitGefahrenklasse3Validation,
  isBerufRequiredForPerson,
  uvPersonenValidation
} from '../uvangebot/uvperson.validation';
import { UvAngebotData, UvPerson } from '../uvangebot.model.types';
import { isDefined } from '@mng/mng-ts-helpers';
import { getUvAngebotDataFromContext, getUvPersonFromContext } from './uvangebot-context.helper';
import { getFzKontoFirma, isUvLohnsteuervorteilCase } from './logic/uvtarif-laufzeit.logic';

export const UV_BERUF_KIND = { gefahrenklasse: 1, name: 'Kind', gesundheitstarif: false };

export function createUvPersonenDataModelGroup(): ModelGroup {
  return new ModelGroup('uvPersonenData')
    .withValidation(uvPersonenValidation)
    .addArrayGroupObject(createUvPersonenModelGroup());
}

export function createBerufModelGroup(id = 'berufe'): ModelGroup {
  return new ModelGroup(id, 'ausgeübte Berufe')
    .withInterfaceName('UvBeruf')
    .addItems(
      new StringField('name', 'ausgeübte Beruf'),
      new NumberField('gefahrenklasse').withReadOnly(true),
      new BooleanField('gesundheitstarif').withReadOnly(true)
    );
}

export function createHobbygruppeModelGroup(id = 'hobbygruppen'): ModelGroup {
  return new ModelGroup(id, 'ausgeübte Hobbygruppen')
    .withInterfaceName('UvHobbygruppe')
    .addItems(
      new NumberField('value').withReadOnly(true),
      new StringField('bezeichnung', 'ausgeübte Hobbygruppe').withReadOnly(true)
    );
}

export function createAmateursportgruppeModelGroup(id = 'amateursportgruppen'): ModelGroup {
  return new ModelGroup(id, 'ausgeübte Amateursportgruppen')
    .withInterfaceName('UvAmateursportgruppe')
    .addItems(
      new NumberField('value').withReadOnly(true),
      new StringField('bezeichnung', 'ausgeübte Amateursportgruppe').withReadOnly(true)
    );
}

export function createAmateursportModelGroup(id = 'amateursport'): ModelGroup {
  return new ModelGroup(id, 'ausgeübter Amateursport')
    .withInterfaceName('UvAmateursport')
    .addItems(
      new NumberField('value').withReadOnly(true),
      new StringField('bezeichnung', 'ausgeübte Amateursport').withReadOnly(true)
    );
}

export function createUvPersonenModelGroup(id = 'personen'): ModelGroup {
  const personModelGroup: ModelGroup = new ModelGroup(id, 'UvPerson').withInterfaceName('UvPerson');

  const stammdaten = new StammdatenBase();
  stammdaten.init(true, true);
  stammdaten.adresse.withInterfaceName('UvAdressModelData');

  const stammdatenModelGroup = stammdaten.createStammdatenModelGroup().withInterfaceName('UvStammdaten');

  personModelGroup
    .addItems(
      new StringField('id'),
      stammdatenModelGroup,
      createUvPersonRollenModelGroup(),
      createHauptwohnsitzAttribut(),
      createUvPersonBeziehungenModelGroup(),
      new BooleanField('kindInVertrag', 'Kind in Vertrag').withReadOnly().withRemoveDataWhenNotRelevant(true),
      new BooleanField('gesundheitsfragenNotwendig', 'Gesundheitsfragen notwendig')
        .withReadOnly()
        .withRemoveDataWhenNotRelevant(true)
    )
    .addArrayGroupObject(
      createBerufModelGroup()
        .withFunctionDependency(isBerufRequiredForPersonFunc)
        .withRemoveDataWhenNotRelevant(true)
        .withValidation(berufMitGefahrenklasse3Validation)
        .withRequired()
    )
    .addArrayGroupObject(
      createHobbygruppeModelGroup()
        .withFunctionDependency(isVpAndNatuerlichePersonFunc, false)
        .withRemoveDataWhenNotRelevant(true)
    )
    .addArrayGroupObject(
      createAmateursportgruppeModelGroup()
        .withFunctionDependency(isVpAndNatuerlichePersonFunc, false)
        .withRemoveDataWhenNotRelevant(true)
    )
    .addArrayGroupObject(
      createAmateursportModelGroup()
        .withFunctionDependency(isVpAndNatuerlichePersonFunc, false)
        .withRemoveDataWhenNotRelevant(true)
    );

  return personModelGroup;
}

function isVpAndNatuerlichePersonFunc(dependency: ModelDependency, modelContext: IModelContext) {
  const uvPerson: UvPerson = getUvPersonFromContext(modelContext);
  return !!uvPerson.hauptwohnsitzOesterreich && uvPerson.personRollen.vp && !uvPerson.stammdaten.juristischePerson;
}

function isBerufRequiredForPersonFunc(dependency: ModelDependency, modelContext: IModelContext) {
  const uvAngebotData: UvAngebotData = getUvAngebotDataFromContext(modelContext);
  const uvPerson: UvPerson = getUvPersonFromContext(modelContext);
  return isBerufRequiredForPerson(uvAngebotData, uvPerson);
}

function createUvPersonRollenModelGroup(): ModelGroup {
  return new ModelGroup('personRollen', 'Rollen Person')
    .withInterfaceName('UvPersonRollen')
    .addItems(
      new BooleanField('vn', 'VN').withReadOnly(),
      new BooleanField('vp', 'VP').withReadOnly(),
      new BooleanField('kind', 'Kind').withReadOnly()
    );
}

function createHauptwohnsitzAttribut(): BooleanField {
  const desc = 'Haben Sie Ihren Hauptwohnsitz und Lebensmittelpunkt in Österreich?';
  const id = 'hauptwohnsitzOesterreich';
  return new BooleanField(id, desc)
    .withFunctionDependency((dependency: ModelDependency, modelContext: IModelContext) => {
      const uvPerson: UvPerson = getUvPersonFromContext(modelContext);
      return uvPerson.personRollen?.vn || uvPerson.personRollen?.vp;
    })
    .withValidation((modelItem: ModelItem, modelContext: IModelContext) => {
      const hautpwohnSitzOesterreich = modelContext.value;

      if (isDefined(hautpwohnSitzOesterreich) && !hautpwohnSitzOesterreich) {
        return {
          id,
          message: 'Für dieses Produkt ist ein Hauptwohnsitz und der Lebensmittelpunkt in Österreich erforderlich!'
        } as IValidationResult;
      } else {
        return null;
      }
    });
}

export const UV_VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE = '2';
export const UV_VN_BEZ_OPTION_VALUE_KIND = '3';
export const UV_VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER = '5';

function createUvPersonBeziehungenModelGroup(): ModelGroup {
  const id = 'vnBeziehung';
  return new ModelGroup('personBeziehungen', 'Beziehungen').withInterfaceName('UvPersonBeziehungen').addItems(
    new StringField(id, 'Beziehung zum Versicherungsnehmer')
      .withRemoveDataWhenNotRelevant()
      .withValueOptions(
        new ValueOptions([
          { name: 'Ehegatte/Lebensgefährte', value: UV_VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE },
          { name: 'Kind', value: UV_VN_BEZ_OPTION_VALUE_KIND },
          { name: 'Kein Angehöriger', value: UV_VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER }
        ])
      )
      .withValidation((modelItem: ModelItem, modelContext: IModelContext) => {
        const uvAngebotData: UvAngebotData = getUvAngebotDataFromContext(modelContext);
        if (isUvLohnsteuervorteilCase(uvAngebotData)) {
          const uvPerson: UvPerson = getUvPersonFromContext(modelContext);
          if (uvPerson.personRollen?.vp && !uvPerson.personRollen?.vn) {
            if (!uvPerson.personBeziehungen?.vnBeziehung) {
              return {
                id,
                message: 'Die Beziehung zum Versicherungsnehmer muss definiert werden!'
              } as IValidationResult;
            } else if (uvPerson.personBeziehungen?.vnBeziehung === UV_VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER) {
              return {
                id,
                message: `Die Nutzung des Lohnsteuervorteils ist nur Angehörigen des VN in der "${getFzKontoFirma(uvAngebotData)}" vorbehalten!`
              } as IValidationResult;
            }
          }
        }
        return null;
      })
      .withRequiredFunction((modelContext: IModelContext) => {
        const uvAngebotData: UvAngebotData = getUvAngebotDataFromContext(modelContext);
        if (isUvLohnsteuervorteilCase(uvAngebotData)) {
          const uvPerson: UvPerson = getUvPersonFromContext(modelContext);
          if (uvPerson.personRollen?.vp && !uvPerson.personRollen?.vn) {
            return true;
          }
        }
        return false;
      })
  );
}
